import { cloneDeep, isNil, sumBy } from 'lodash';

import {
  FieldSelectOption,
  OptionItem
} from '@issue-detail/components/IssueDrawer/Model/field';
import { IssueSubTask } from '@issue-detail/components/IssueDrawer/Model/Issue';
import { toRaw } from '@vue/composition-api';

// fn: (option: OptionItem) => any
const getOption = (options: OptionItem[], value: any) =>
  options?.find(item => item.value === value);

const commonTransform = (
  value: any,
  options: OptionItem[],
  fn: (option: OptionItem) => Record<string, any> = () => ({})
) => {
  const option = getOption(options, value);
  return {
    id: value,
    name: option?.name,
    ...(option ? fn(option) : {})
  };
};
const commonArrayTransform = (
  values: any[],
  options: OptionItem[],
  fn: (option: OptionItem) => Record<string, any> = () => ({})
) => values.map(val => commonTransform(val, options, fn));

const customTransform = (value: any, options: OptionItem[]) =>
  Array.isArray(value)
    ? commonArrayTransform(value, options)
    : commonTransform(value, options);

export const transforms: Record<
  string,
  (value: any, options: OptionItem[]) => any
> = {
  assignee(value, options) {
    return commonTransform(value, options, option => ({
      avatar: option?.extendValue
    }));
  },
  owner(value, options) {
    return commonTransform(value, options);
  },
  follows(value, options) {
    return commonArrayTransform(value, options);
  },
  label(value, options) {
    return commonArrayTransform(value, options);
  },
  issueTypeId(value, options) {
    return commonTransform(value, options, option => ({
      iconUrl: option?.icon
    }));
  },
  status(value, options) {
    return commonTransform(value, options, option => ({
      stateCategory: option.extendValue
    }));
  },
  sprint(value, options) {
    return commonTransform(value, options);
  },
  releaseVersion(value, options) {
    return commonTransform(value, options);
  },
  epic(value, options) {
    return commonTransform(value, options);
  },
  priority(value, options) {
    return commonTransform(value, options, option => ({
      color: option?.extendValue,
      sequence: option?.sequence
    }));
  }
};

export const ignoreKeys = [
  'activities',
  // 'attachment',
  'createBy',
  'issueNumber',
  'relationship',
  'subtask',
  'updateBy',
  'workflowId'
];

const getOptions = (
  key: string,
  selectOptionsMap: Record<string, OptionItem[]>
) => {
  return selectOptionsMap[key];
};

export const transformVO = (
  data: Record<string, any>,
  selectOptionsMap: Record<string, OptionItem[]>,
  currentUserId: number,
  subtask: IssueSubTask[]
) => {
  const result = Object.keys(data)
    .filter(k => !ignoreKeys.includes(k) && !k.endsWith('VO'))
    .reduce(
      (a, c) => {
        const isCustom = c.startsWith('customfield_');
        const transform = isCustom
          ? customTransform
          : (transforms[c] as
              | ((value: any, options: OptionItem[]) => any)
              | undefined);
        const key = transform ? `${c}VO` : c;
        const v = data[c];
        let value =
          !isNil(v) && transform ? transform(v, selectOptionsMap[c] || []) : v;

        if (!isNil(value) && isNil(value.name) && v === data[`${c}VO`]?.id) {
          value = toRaw(data[`${c}VO`]);
        }

        a[key] = value;
        if (c !== key) {
          a[c] = v;
        }
        return a;
      },
      {
        updateBy: currentUserId,
        updateByVO: {
          id: currentUserId,
          optTime: Date.now()
        },
        createBy: data.createBy || currentUserId,
        createByVO: {
          id: data.createBy || currentUserId
        },
        subTaskVOList: subtask?.map(task => ({
          ...task,
          assigneeVO: task.assignee
            ? transforms.assignee(
                task.assignee,
                getOptions('assignee', selectOptionsMap)
              )
            : null,
          status: {
            id: task.status,
            stateCategory: task.stateCategory || task.status.stateCategory
          },
          statusId: task.status
        }))
      } as Record<string, any>
    );

  result.subtask = result.subTaskVOList;
  result.totalRemainingLabour =
    isNil(data.remainingLabour) &&
    (subtask || []).every(item => isNil(item.remainingLabour))
      ? undefined
      : sumBy(subtask || [], item => item.remainingLabour || 0) +
        (data.remainingLabour || 0);

  result.totalActualLabour =
    isNil(data.actualLabour) &&
    (subtask || []).every(item => isNil(item.actualLabour))
      ? undefined
      : sumBy(subtask || [], item => item.actualLabour || 0) +
        (data.actualLabour || 0);

  if (result.autoCalculateFlag === 1) {
    result.estimatePoint = (subtask || []).reduce((total, item) => {
      return total + (item.estimatePoint || 0);
    }, 0);
  }

  return cloneDeep(result);
};
